import React, { useContext, useEffect, useState } from 'react';
import type { GetServerSidePropsContext, NextPage } from 'next';
import { useServiceRef } from 'hooks';
import {
  Category,
  ContentService,
  FullWidthCarousel,
  ProductsResponse,
  ImageTransformation,
  ProductService,
  HomePageContent
} from '@services';
import { Button, ScrollableProductCards } from '@components';
import { ContentCarousel, breakpoints, useMediaQuery } from 'gd-design-library';
import styles from './styles/home.module.scss';
import classnames from 'classnames/bind';
import { useRouter } from 'next/router';
import {
  ALL_IMAGES_TRANSFORMATIONS,
  CartContext,
  CategoriesTreeContext,
  pushSelectPromotionEvent,
  pushViewPromotionEvent
} from '@utils';
import { Cart, CartService } from '@services/cart';
import { LineItemDraft } from '@commercetools/platform-sdk';
import CTLogo from '@assets/images/partner-logos/contentstack.svg';
import MXPLogo from '@assets/images/partner-logos/mxp.png';
import AlgoliaLogo from '@assets/images/partner-logos/algolia.png';
import { isFeatureEnabled, FEATURES } from '@utils/featureFlags';
import { Product, ProductsRecommendations } from '@services/product/interfaces';
import { ProductDataContainer } from '@services/content/interfaces';
import { getCartDraft } from '@utils/getCartDraft';
import { TenantService } from '@services/tenant';
import Image from 'next/image';

const cx = classnames.bind(styles);

const CN = 'home-page-container';

const carouselClassName = `${CN}__carousel`;
const carouselSlideClassName = `${carouselClassName}-slide`;
const carouselSlideBackgroundClassName = `${carouselSlideClassName}-gradient`;
const carouselSlideTitleClassName = `${carouselSlideClassName}-title`;
const carouselSlideDescriptionClassName = `${carouselSlideClassName}-description`;
const carouselSlideImageClassName = `${carouselSlideClassName}-image`;
const carouselSlideInfoClassName = `${carouselSlideClassName}-info`;
const carouselSlideButtonClassName = `${carouselSlideClassName}-button`;
const subcategoryProductsClassName = `${CN}__subcategory-products`;
const featuredProductsClassName = `${CN}__featured-products`;

interface Props {
  content: HomePageContent;
  products: Product[];
}

const HomePage: NextPage = (props: React.PropsWithChildren<Props>) => {
  const productServiceRef = useServiceRef(ProductService);

  const [ topCarouselContent, setTopCarouselContent ] = useState<FullWidthCarousel[] | null>(null);
  const [ featuredProducts, setFeaturedProducts ] = useState<Product[] | null>(null);
  const [ fitnessApparelProducts, setFitnessApparelProducts ] =
    useState<(ProductsResponse | ProductsRecommendations | null)[]>([]);
  const [ sportNutritionProducts, setSportNutritionProducts ] =
    useState<(ProductsResponse | ProductsRecommendations | null)[]>([]);

  const { push } = useRouter();

  const { categories } = useContext(CategoriesTreeContext);
  const fitnessApparelSubcategories = categories.find((category: Category) => category.name === 'Fitness Apparel')?.children;
  const sportsNutritionSubcategories = categories.find((category: Category) => category.name === 'Sports Nutrition')?.children;

  const cartContext = useContext(CartContext);
  const cartServiceRef = useServiceRef(CartService);
  const imageTransformation = new ImageTransformation();
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.md})`);

  const handleAddToCart = async (lineItemDraft: LineItemDraft, currency?: string): Promise<void> => {
    let cart: Cart | null;
    const { cart: currentCart, setCart } = cartContext;
    if (!currentCart) {
      const cartDraft = getCartDraft(lineItemDraft, currency);
      cart = await cartServiceRef.current.createCart(cartDraft);
    } else {
      cart = await cartServiceRef.current.addLineItem(
        currentCart?.id,
        currentCart?.version,
        lineItemDraft
      );
    }
    if (cart) {
      setCart(cart);
    }
  };

  const renderSubcategories = (products: (ProductsRecommendations | null)[]): (React.ReactElement | undefined)[] =>
    products?.filter(category => category?.results?.length).map((category, index) => (
      <div key={ index } className={ cx(subcategoryProductsClassName) }>
        <ScrollableProductCards
          onAddToCart={ handleAddToCart }
          products={ category }
          title={ category.categoryName }
          logo={ process.env.NEXT_PUBLIC_SEARCH_ENGINE === 'mxp' ? MXPLogo.src : AlgoliaLogo.src }
          analyticsContext={ {
            listId: `subcategory-products-${category.categoryName}`,
            listName: `Subcategory Products - ${category.categoryName}`
          } }
        />
      </div>
    ));

  useEffect(() => {
    setTopCarouselContent(props?.content?.top_carousel ?? null);

    const ids = props?.content?.featured_products[0]?.products;
    if (ids) {
      setFeaturedProducts(props?.products);
    }
  }, []);

  useEffect(() => {
    (async (): Promise<void> => {
      if (process.env.NEXT_PUBLIC_SEARCH_ENGINE !== 'mxp') {
        if (fitnessApparelSubcategories?.length) {
          const productsPromises = fitnessApparelSubcategories.map(async category => {
            const products = await productServiceRef.current.getProductRecommendationsAWS(category.id);

            return { results: products, categoryName: category.name };
          });
          const productRecommendations = await Promise.all(productsPromises);
          setFitnessApparelProducts(productRecommendations);
        }
        if (sportsNutritionSubcategories?.length) {
          const productsPromises = sportsNutritionSubcategories.map(async category => {
            const products = await productServiceRef.current.getProductRecommendationsAWS(category.id);

            return { results: products, categoryName: category.name };
          });
          const productRecommendations = await Promise.all(productsPromises);
          setSportNutritionProducts(productRecommendations);
        }
      }

      if (isFeatureEnabled(FEATURES.MULTI_TENANCY)) {
        if (fitnessApparelSubcategories?.length) {
          const productsPromises = fitnessApparelSubcategories.map(async category => {
            const products = await productServiceRef.current.getProductRecommendations(`Fitness Apparel > ${category.name}`);

            return { results: products, categoryName: category.name };
          });
          const productRecommendations = await Promise.all(productsPromises);
          setFitnessApparelProducts(productRecommendations);
        }

        if (sportsNutritionSubcategories?.length) {
          const productsPromises = sportsNutritionSubcategories.map(async category => {
            const products = await productServiceRef.current.getProductRecommendations(`Sports Nutrition > ${category.name}`);

            return { results: products, categoryName: category.name };
          });
          const productRecommendations = await Promise.all(productsPromises);
          setSportNutritionProducts(productRecommendations);
        }
      }
    })();
  }, [ fitnessApparelSubcategories, sportsNutritionSubcategories ]);

  useEffect(() => {
    topCarouselContent?.[0]?.slides.forEach(slideContent => {
      pushViewPromotionEvent(slideContent.title);
    });
  }, [ topCarouselContent ]);

  return (
    <div className={ cx(CN) }>
      {topCarouselContent && (
        <div className={ cx(carouselClassName) }>
          {topCarouselContent[0]?.slides && (
            <ContentCarousel
              showArrows={ false }
              items={ topCarouselContent[0]?.slides }
              renderItem={ (slideContent): React.ReactElement => {
                const cloudinaryImageUrl = imageTransformation.transformById(
                  slideContent.cloudinary_image?.[0]?.public_id,
                  ALL_IMAGES_TRANSFORMATIONS,
                  slideContent.image?.url
                );
                const cloudinaryImageMobileUrl = imageTransformation.transformById(
                  slideContent.cloudinary_image_mobile?.[0]?.public_id,
                  ALL_IMAGES_TRANSFORMATIONS,
                  slideContent.image_mobile?.url
                );

                return (
                  <div className={ cx(carouselSlideClassName) } key={ slideContent.description }>
                    <div className={ cx(carouselSlideBackgroundClassName) } />
                    <Image
                      src={ isMobile ? cloudinaryImageMobileUrl : cloudinaryImageUrl }
                      className={ cx(carouselSlideImageClassName) }
                      alt={ slideContent.title }
                      width={ 0 }
                      height={ 0 }
                      sizes='100%'
                      fetchPriority='high'
                      priority
                    />
                    <div className={ cx(carouselSlideInfoClassName) }>
                      <div className={ cx(carouselSlideTitleClassName) }>
                        { slideContent.title }
                      </div>
                      <div className={ cx(carouselSlideDescriptionClassName) }>
                        { slideContent.description }
                      </div>
                      <Button
                        onClick={ (): void => {
                          pushSelectPromotionEvent(slideContent.title);
                          push(slideContent.link.href);
                        } }
                        className={ cx(carouselSlideButtonClassName) }
                      >
                        { slideContent.link.title }
                      </Button>
                    </div>
                  </div>
                );
              } }
            />
          )}
        </div>
      )}

      <div className={ cx(featuredProductsClassName) }>
        <ScrollableProductCards
          onAddToCart={ handleAddToCart }
          products={ { results: featuredProducts, categoryName: 'Featured Products' } }
          title={ 'Featured Products' }
          logo={ CTLogo.src }
          analyticsContext={ {
            listId: 'featured-products',
            listName: 'Featured Products'
          } }
        />
      </div>

      {renderSubcategories(fitnessApparelProducts as ProductsRecommendations[])}
      {renderSubcategories(sportNutritionProducts as ProductsRecommendations[])}
    </div>
  );
};

export default HomePage;

export const getServerSideProps = async (context: GetServerSidePropsContext): Promise<{ props: Props }> => {
  const tenantServiceRef = useServiceRef(TenantService);
  let selectedTenantKey = '';
  if (isFeatureEnabled(FEATURES.MULTI_TENANCY)) {
    const cookieValue = context?.req?.cookies?.selectedTenant;
    if (cookieValue) {
      selectedTenantKey = JSON.parse(cookieValue)?.key;
    } else {
      const tenantList = await tenantServiceRef.current.getTenants();
      selectedTenantKey = tenantList?.[0].key;
    }
  }
  const contentServiceRef = useServiceRef(ContentService);
  const productService = useServiceRef(ProductService);

  const content = await contentServiceRef.current.getHomepage(selectedTenantKey);

  let products: Product[] | null = null;

  const featuredProducts = content?.featured_products[0]?.products as ProductDataContainer;
  if (featuredProducts && featuredProducts.data && isFeatureEnabled(FEATURES.MULTI_TENANCY)) {
    const idList = featuredProducts.data.map(item => item.id);
    products = await productService.current.getProductDataByIds(idList);
  }

  const featuredAWSProducts = content?.featured_products[0]?.products as string[];
  if (featuredAWSProducts && process.env.NEXT_PUBLIC_SEARCH_ENGINE !== 'mxp') {
    products = await productService.current.getProductDataByIds(featuredAWSProducts);
  }

  return {
    props: {
      products,
      content
    }
  };
};
